import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { API } from '@/App';
import { isMysteryShoppingSurvey } from '@/components/SurveyExperienceSetting';
import { AccessProfileDialog } from '@/components/surveyAccess/AccessProfileDialog';

const summarize = (p) => {
  const methods = [p.methods?.qr && 'QR', p.methods?.manual && 'Manual code'].filter(Boolean).join(' + ');
  const h = p.validity_hours;
  const validity = h ? (h % 24 === 0 ? `valid ${h / 24} day${h === 24 ? '' : 's'}` : `valid ${h}h`) : 'no expiry';
  const mapped = (p.branch_mapping || []).length;
  return [methods, p.required ? 'required' : 'optional', validity, p.single_use ? 'single-use' : 'reusable', p.timezone,
    `${mapped} branch${mapped === 1 ? '' : 'es'}`].join(' · ');
};

// Phase 3 — Survey Settings block: survey access validation (OFF by default).
export const SurveyAccessSetting = ({ survey, branches, onChange }) => {
  const [profiles, setProfiles] = useState([]);
  const [editing, setEditing] = useState(null);
  const av = survey?.access_validation || {};
  const enabled = !!av.enabled;
  const profileId = av.profile_id || '';

  const load = useCallback(async () => {
    const res = await fetch(`${API}/access-profiles`, { credentials: 'include' }).catch(() => null);
    if (res?.ok) setProfiles(await res.json());
  }, []);
  useEffect(() => { load(); }, [load]);

  if (isMysteryShoppingSurvey(survey)) return null;
  const selected = profiles.find(p => p.profile_id === profileId);

  const toggle = (on) => {
    if (on && !profileId) { toast.error('Select or create an Access Profile first'); return; }
    onChange({ enabled: on, profile_id: profileId || null });
  };
  const onSaved = (p) => {
    load();
    setEditing(null);
    if (!profileId) onChange({ enabled, profile_id: p.profile_id });
  };

  return (
    <div className="space-y-2" data-testid="settings-survey-access">
      <div className="flex items-center justify-between gap-3">
        <label className="text-xs font-semibold text-[#334155]">Survey Access Validation</label>
        <Switch checked={enabled} onCheckedChange={toggle} data-testid="settings-access-enabled-switch" />
      </div>
      <p className="text-[11px] text-[#94A3B8]">
        {enabled ? 'On — respondents need a valid QR link or feedback code.' : 'Off — anyone with the survey link can respond.'}
      </p>
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <Select value={profileId} onValueChange={(id) => onChange({ enabled, profile_id: id })}>
            <SelectTrigger data-testid="settings-access-profile-select"><SelectValue placeholder="Select an Access Profile" /></SelectTrigger>
            <SelectContent>
              {profiles.map(p => (
                <SelectItem key={p.profile_id} value={p.profile_id} data-testid={`access-profile-option-${p.profile_id}`}>{p.name}{p.is_default && profiles.length > 1 ? ' (default)' : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => setEditing({})} data-testid="access-profile-new-btn">New</Button>
        {selected && (
          <Button type="button" variant="outline" size="sm" onClick={() => setEditing(selected)} data-testid="access-profile-edit-btn">Edit</Button>
        )}
      </div>
      {selected && <p className="text-[11px] text-[#64748B]" data-testid="settings-access-summary">{summarize(selected)}</p>}
      {editing && (
        <AccessProfileDialog profile={editing} branches={branches} profileCount={profiles.length} onClose={() => setEditing(null)} onSaved={onSaved} />
      )}
    </div>
  );
};
